import CheckBoxList, {
  type CheckBoxListOption,
} from "../../../Forms/Controls/CheckBoxList/CheckBoxList";
import {
  Controller,
  useFormContext,
  type ControllerRenderProps,
  type ControllerFieldState,
} from "react-hook-form";
import React, { useEffect, useRef, useState } from "react";
import { sortBy } from "lodash-es";
import { useQuery, type WatchQueryFetchPolicy } from "@apollo/client";
import {
  GET_COSTUMER_CSV_COLUMNS,
  GET_CSV_COLUMNS_OPTIONS,
} from "../DownloadReportApi";
import "./ReportFields.scss";
import { ticketEndpointTypeEnum } from "@SeemplicityDev/ticket-master";
import useReportParams from "../useReportParams";
import {
  filterEntityEnum,
  toLegacyEntityKey,
  type FilterEntity,
} from "../../FilterManagement/filterEntity";
import { useTranslation } from "react-i18next";

import { REQUIRED_ERROR_MESSAGE } from "@SeemplicityDev/shared";

const CSV_COLUMNS_STORAGE_KEY = "csv_columns_selection";

type EndpointType =
  (typeof ticketEndpointTypeEnum)[keyof typeof ticketEndpointTypeEnum];

/** A single column definition from the `reports_menu` report options. */
interface CSVColumnField {
  key: string;
  header_name: string;
}

interface CSVColumnsOptionsData {
  reports_menu?: {
    report_options?: Array<{
      type?: string;
      description?: string;
      fields?: CSVColumnField[];
    } | null> | null;
  } | null;
}

interface CustomerCSVColumnsData {
  /** JSON string parsed into `{ fields: string[] }`. */
  default_report_parameters?: string | null;
}

/** Selected column option rendered by the {@link CheckBoxList}. */
interface ColumnOption extends CheckBoxListOption {
  label?: string;
  value: string;
  name: string;
}

export interface ReportFieldsProps {
  rmqId?: string;
  endpointType?: EndpointType;
  additionalSelectedColumns?: string[];
  description?: string;
  entityType?: FilterEntity;
  label?: string;
  fetchPolicy?: WatchQueryFetchPolicy;
  useLocalStorage?: boolean;
}

const ReportFields: React.FC<ReportFieldsProps> = ({
  rmqId,
  endpointType = ticketEndpointTypeEnum.TICKET,
  additionalSelectedColumns = [],
  description = "",
  entityType = filterEntityEnum.REMEDIATION_ITEM,
  label = "CSV Columns",
  fetchPolicy = "no-cache",
  useLocalStorage = true,
}) => {
  const [columnsOptions, setColumnsOptions] = useState<ColumnOption[] | null>(
    null
  );
  const { reportType } = useReportParams(entityType);
  const { t } = useTranslation();

  const storageKey = `${CSV_COLUMNS_STORAGE_KEY}_${toLegacyEntityKey(entityType).toLowerCase()}`;

  const getStoredColumns = (): string[] | null => {
    try {
      const stored = localStorage.getItem(storageKey);
      return stored ? (JSON.parse(stored) as string[]) : null;
    } catch (e) {
      console.error("Error reading from localStorage:", e);
      return null;
    }
  };

  const saveColumnsToStorage = (columns: string[]): void => {
    try {
      localStorage.setItem(storageKey, JSON.stringify(columns));
    } catch (e) {
      console.error("Error saving to localStorage:", e);
    }
  };

  const storedColumns = getStoredColumns();
  const shouldSkipQuery = useLocalStorage && !!storedColumns && !rmqId;

  const { data: CSVColumnsData } = useQuery<CSVColumnsOptionsData>(
    GET_CSV_COLUMNS_OPTIONS
  );
  const { data: customerCSVColumnsData } = useQuery<CustomerCSVColumnsData>(
    GET_COSTUMER_CSV_COLUMNS,
    {
      fetchPolicy,
      skip: shouldSkipQuery,
      variables: {
        remediation_queue_id: rmqId || undefined,
        endpoint_type: endpointType,
        report_type: reportType,
      },
    }
  );

  const { control, resetField } = useFormContext();

  const seededReportTypeRef = useRef<string | null>(null);

  useEffect(() => {
    if (!!CSVColumnsData && (!!customerCSVColumnsData || shouldSkipQuery)) {
      const CSVColumnsOptions =
        CSVColumnsData?.reports_menu?.report_options?.find(
          (option) => option?.type === reportType
        )?.fields;
      let columnsValuesOptions: ColumnOption[] = [];
      if (!!CSVColumnsOptions) {
        columnsValuesOptions = CSVColumnsOptions?.map((option) => ({
          label: option?.header_name,
          value: option?.key,
          name: option?.key,
        }));
      }

      let costumerColumnsValues: string[] | undefined;
      if (shouldSkipQuery) {
        costumerColumnsValues = storedColumns ?? undefined;
      } else {
        try {
          costumerColumnsValues = (
            JSON.parse(
              customerCSVColumnsData?.default_report_parameters ?? ""
            ) as { fields?: string[] }
          )?.fields;
        } catch (e) {}
      }

      costumerColumnsValues = (costumerColumnsValues ?? []).filter((value) =>
        CSVColumnsOptions?.find((opt) => opt.key === value)
      );

      additionalSelectedColumns?.forEach((column) => {
        if (!costumerColumnsValues?.find((option) => option === column)) {
          costumerColumnsValues?.push(column);
        }
      });

      const sortedOptionsByCostumerValues = sortBy(
        columnsValuesOptions,
        (option) => {
          const costumerOptionIndex = costumerColumnsValues?.indexOf(
            option?.value
          );
          if (costumerOptionIndex !== undefined && costumerOptionIndex > -1) {
            return costumerOptionIndex;
          }

          return costumerColumnsValues?.length;
        }
      );

      setColumnsOptions(sortedOptionsByCostumerValues);

      if (seededReportTypeRef.current !== reportType) {
        seededReportTypeRef.current = reportType ?? null;
        resetField("columns", {
          defaultValue: costumerColumnsValues,
        });
      }
    }
  }, [CSVColumnsData, customerCSVColumnsData, reportType, shouldSkipQuery]);

  return (
    <Controller
      name={"columns"}
      render={({
        field: { onChange, value },
        fieldState: { error },
      }: {
        field: ControllerRenderProps;
        fieldState: ControllerFieldState;
      }) => {
        const handleChange = (newValue: string[]): void => {
          onChange(newValue);
          saveColumnsToStorage(newValue);
        };

        return (
          <div className={"csv-columns-wrapper"}>
            <CheckBoxList
              label={t(label)}
              data-testid={"download-csv-columns"}
              loading={!columnsOptions}
              options={columnsOptions ?? undefined}
              sortable
              error={error}
              withBorder
              description={description}
              withBg
              onChange={handleChange}
              value={value}
              direction={"column"}
              searchable
              hasSelectAll
              selectAllLabel={t("Select All")}
            />
          </div>
        );
      }}
      control={control}
      rules={{
        validate: (value) => !!value?.length || REQUIRED_ERROR_MESSAGE,
      }}
    />
  );
};
export default ReportFields;
